'use client'

import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { BellIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { ChevronDownIcon as ChevronDownIconSolid } from '@heroicons/react/24/solid'
import { useRouter, usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'
import type { User } from 'firebase/auth'
import { onAuthStateChanged, signOut } from 'firebase/auth'
import { auth } from '~/components/FirebaseProvider'
import { api } from '~/trpc/react'
import { Avatar } from '~/components/DefaultAvatar'
import { getPublicAvatarSrc } from '~/lib/utils/avatar'

export function UserMenu({ hideNotifications = false }: { hideNotifications?: boolean }) {
    const router = useRouter()
    const pathname = usePathname()
    const [user, setUser] = useState<User | null>(null)
    const [loading, setLoading] = useState(true)
    const isProfilePage = pathname?.startsWith('/app/profile') ?? false

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setUser(user)
            setLoading(false)
        })

        return () => unsubscribe()
    }, [])

    // Get user data from database to check roles
    // Pass Firebase user info for auto-creation if user doesn't exist
    const { data: userData } = api.post.getUserByFirebaseUid.useQuery(
        { 
            firebaseUid: user?.uid ?? '',
            email: user?.email ?? undefined,
            displayName: user?.displayName ?? undefined,
        },
        { enabled: !!user?.uid }
    )

    const isAdmin = userData?.roles?.includes('ADMIN') ?? false

    const handleSignOut = async () => {
        try {
            await signOut(auth)
            router.push('/')
        } catch (error) {
            console.error('Error signing out:', error)
        }
    }

    const handleProfileClick = () => {
        router.push('/app/profile')
    }

    const handleConsoleClick = () => {
        router.push('/app/console')
    }

    const userNavigation = [
        { name: 'Your Profile', onClick: handleProfileClick },
        ...(isAdmin ? [{ name: 'Console', onClick: handleConsoleClick }] : []),
        { name: 'Sign out', onClick: handleSignOut },
    ]

    // Show loading state
    if (loading) {
        return (
            <div className="flex items-center">
                <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary"></div>
            </div>
        )
    }

    // If no user, don't render anything
    if (!user) {
        return null
    }

    return (
        <div className="flex items-center gap-x-4">
            {!hideNotifications && (
                <button
                    type="button"
                    className="relative rounded-full bg-white p-1 text-gray-400 hover:text-secondary focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:outline-hidden"
                >
                    <span className="absolute -inset-1.5" />
                    <span className="sr-only">View notifications</span>
                    <BellIcon aria-hidden="true" className="size-6" />
                </button>
            )}

            {/* Profile dropdown */}
            <Menu as="div" className="relative z-[100] flex flex-col items-center gap-x-4">
            <div className="relative">
                    <MenuButton className="relative flex flex-col items-center max-w-xs rounded-full text-sm cursor-pointer focus:outline-hidden focus:ring-0 focus-visible:ring-0 group">
                        <span className="absolute -inset-1.5 h-[44px]" />
                        <span className="sr-only">Open user menu</span>
                        <Avatar
                            src={getPublicAvatarSrc(userData, user.photoURL)}
                            alt={user.displayName ?? user.email ?? 'User'}
                            size={32}
                            className="size-16"
                            verified={userData?.verified ?? false}
                        />
                        <div className="absolute bottom-[-22px] w-full flex flex-col items-center pb-2">
                            {isProfilePage ? (
                                <ChevronDownIconSolid className="h-4 w-4 text-primary group-hover:text-secondary" />
                            ) : (
                                <ChevronDownIcon className="h-4 w-4 text-gray-500 group-hover:text-secondary" />
                            )}
                            {isProfilePage ? (
                                <div className="absolute w-8 border-b-4 border-sage mt-1 bottom-[5px] group-hover:border-secondary"></div>
                            ) : (
                                <div className="absolute w-8 border-b-4 border-transparent mt-1 bottom-[5px] group-hover:border-secondary"></div>
                            )}
                        </div>
                    </MenuButton>
                </div>
                <MenuItems
                    transition
                    className="absolute right-0 z-[100] top-full mt-4 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 transition focus:outline-hidden data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-200 data-enter:ease-out data-leave:duration-75 data-leave:ease-in"
                >
                    {userNavigation.map((item) => (
                        <MenuItem key={item.name}>
                            <button
                                onClick={item.onClick}
                                className="block w-full px-4 py-2 text-left text-sm text-gray-700 data-focus:bg-secondary/10 data-focus:text-secondary data-focus:outline-hidden"
                            >
                                {item.name}
                            </button>
                        </MenuItem>
                    ))}
                </MenuItems>
            </Menu>
        </div>
    )
} 